import { type RefObject, useEffectEvent, useLayoutEffect, useRef } from "react";
import { EASE_OUT, MOTION_BASE_MS } from "../motion";

export function useExploratoryHandoff(
  exploring: boolean,
  scrolledUp: boolean,
  scrollRef: RefObject<HTMLDivElement | null>,
  transcriptRef: RefObject<HTMLElement | null>
) {
  const positionRef = useRef<number | undefined>(undefined);
  const preservePosition = useEffectEvent(() => scrolledUp);
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const transcript = transcriptRef.current;
    if (!scroll || !transcript) return;
    if (exploring) {
      const rememberPosition = () => {
        positionRef.current = transcript.getBoundingClientRect().top;
      };
      rememberPosition();
      scroll.addEventListener("scroll", rememberPosition, { passive: true });
      const observer = new ResizeObserver(rememberPosition);
      observer.observe(scroll);
      observer.observe(transcript);
      return () => {
        scroll.removeEventListener("scroll", rememberPosition);
        observer.disconnect();
      };
    }

    const previousTop = positionRef.current;
    positionRef.current = undefined;
    if (previousTop === undefined) return;
    if (preservePosition()) {
      scroll.scrollTop += transcript.getBoundingClientRect().top - previousTop;
    } else {
      scroll.scrollTop = scroll.scrollHeight;
    }
    const offset = previousTop - transcript.getBoundingClientRect().top;
    if (
      Math.abs(offset) < 1 ||
      !transcript.animate ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const animation = transcript.animate(
      [
        { transform: `translateY(${offset}px)` },
        { transform: "translateY(0)" },
      ],
      { duration: MOTION_BASE_MS, easing: EASE_OUT }
    );
    return () => animation.cancel();
  }, [exploring, scrollRef, transcriptRef]);
}
